import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  getTranslatedText,
  AppContext,
  useIsAuthorisedHook,
  AccessControl,
  useCheckFeatureGating
} from '@nile/components';
import { withRouter } from 'react-router-dom';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { useIntl } from 'react-intl';
import ChartHome from '@nile/reports/src/modules/charts/components/pages/ChartHome';
import { Button, ButtonGroup, TextStyle } from '@superops/ui-design-system';
import { savePersonalization } from '@nile/graphql';
import {
  DEFAULT_VIEW,
  CUSTOM_VIEW,
  DATA_REPORT,
  METRIC_REPORT,
  DATA_REPORT_TYPE,
  PDF_REPORT,
  CHARTS,
  EFFICIENCY_REPORT,
  REPORT_PATHNAME,
  PROFITABLITY_REPORT,
  RETAINER,
  NEWREPORTID,
  INVOICE_STATEMENTS
} from '@nile/reports/src/modules/home/constants';
import Section from '@nile/reports/src/modules/home/components/subpages/NavigationContainer/Section';
import DataReportCascade from '@nile/reports/src/modules/DataReports/components/pages/DataReportCascade';
import PDFConfigurationModal from '@nile/reports/src/modules/reports/components/subpages/PDFConfiguration';
import GrowthReportConfig from '@nile/reports/src/modules/EfficiencyReport/components/subpages/ConfigurationModal';
import { EFFICIENCY_REPORTS } from '../../../constants/index';

const NavigationContainer = (props) => {
  const {
    history,
    chartViews,
    reports,
    setActiveSection,
    activeReportLoading,
    activeViewLoading,
    activeView,
    setActiveView,
    loading
  } = props;
  const intl = useIntl();

  const tabContext = useContext(TabContext);
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const user = {
    firstName: currentUser.firstName,
    userId: currentUser.userId,
    email: currentUser.email,
    lastName: currentUser.lastName
  };

  const [openChart, setOpenChart] = useState(false);

  const [openDataReport, setOpenDataReport] = useState(false);

  const [openConfigurationModal, setOpenConfigurationModal] = useState(false);
  const [openProfitablityModal, setOpenProfitablityModal] = useState(false);

  const isEditAuthorised = useIsAuthorisedHook('report.edit');
  const isRetainerReportAuthorised = useIsAuthorisedHook(
    'retainerReport.allow'
  );
  const isNewContractEnabled = useCheckFeatureGating('CONTRACT_V3')?.enabled;
  const contractReportsAvailable = useCheckFeatureGating('CONTRACT_REPORTS')
    ?.enabled;
  const closeChartModal = () => {
    setOpenChart(false);
    if (tabContext?.activeTab?.state?.openModal) {
      tabContext.updateTab({
        module: 'REPORTS_CHARTS_HOME',
        state: {
          openModal: false
        }
      });
    }
  };

  const closeDataReportModal = () => {
    setOpenDataReport(false);
    if (tabContext?.activeTab?.state?.openModal) {
      tabContext.updateTab({
        module: 'REPORTS_CHARTS_HOME',
        state: {
          openDataReportModal: false
        }
      });
    }
  };

  const handleGrowthModalChange = useCallback(() => {
    setOpenProfitablityModal((prevState) => !prevState);
  }, []);

  const handleConfigurationModalChange = useCallback(() => {
    setOpenConfigurationModal((prevState) => !prevState);
  }, []);

  const getChartViews = () => {
    switch (activeView) {
      case DEFAULT_VIEW:
        return chartViews.filter((el) => el.id === '2001');
      default:
        return chartViews;
    }
  };

  const setReportView = (type) => {
    if (!activeViewLoading) {
      if (tabContext?.activeTab?.state?.activeView) {
        tabContext.updateTab({
          module: 'REPORTS_CHARTS_HOME',
          state: {
            activeView: null
          }
        });
      }
      const personalisedData = {
        name: 'ReportView',
        category: 'ReportViewPersonalization',
        value: JSON.stringify(type),
        user: user
      };
      savePersonalization({
        variables: personalisedData,
        input: personalisedData
      });
      setActiveView(type);
    }
  };

  const restrictViewChange =
    activeViewLoading || activeReportLoading || loading;

  const getListOfReports = (type) => {
    const allReports = [...reports];
    const filteredReports = allReports.filter(
      (item) => item?.reportType === type
    );
    const dataPdf = filteredReports.map((el) => ({
      ...el,
      url: `/${REPORT_PATHNAME[el?.reportType || 'report']}/${el.reportId}/view`
    }));
    return dataPdf;
  };

  const getData = (view) => {
    switch (view) {
      case CHARTS:
        return getChartViews().map((el) => ({
          ...el,
          url: `/${CHARTS}/${el.id}/${el.name}`
        }));
      default:
        return [];
    }
  };

  const data = {
    CHARTS: getData(CHARTS),
    report: [
      ...getListOfReports(METRIC_REPORT),
      ...(isRetainerReportAuthorised ? getListOfReports(RETAINER) : []),
      ...getListOfReports(INVOICE_STATEMENTS),
      ...getListOfReports(null)
    ],
    dataReport: getListOfReports(DATA_REPORT_TYPE),
    efficiencyReport: getListOfReports(EFFICIENCY_REPORT),
    profitablityReport: getListOfReports(PROFITABLITY_REPORT),
    pdfReport: getListOfReports(PDF_REPORT)
  };

  const checkForGating = (name, type) => {
    if (type === EFFICIENCY_REPORT) {
      if (EFFICIENCY_REPORTS?.includes(name)) {
        return useCheckFeatureGating('EFFICIENCY_REPORTS')?.enabled;
      }
      return contractReportsAvailable;
    }
  };

  useEffect(() => {
    if (tabContext?.activeTab?.state?.openModal) setOpenChart(true);
  }, [tabContext?.activeTab?.state?.openModal]);

  useEffect(() => {
    if (tabContext?.activeTab?.state?.openDataReportModal)
      setOpenDataReport(true);
  }, [tabContext?.activeTab?.state?.openDataReportModal]);

  return (
    <>
      <div className="mt10 mb20">
        <ButtonGroup segmented fullWidth>
          <Button
            onClick={() => setReportView(DEFAULT_VIEW)}
            outline={activeView === DEFAULT_VIEW}
            disabled={restrictViewChange}
          >
            <TextStyle>
              {getTranslatedText(intl, 'common.label.default')}
            </TextStyle>
          </Button>
          <Button
            onClick={() => setReportView(CUSTOM_VIEW)}
            outline={activeView === CUSTOM_VIEW}
            disabled={
              !useCheckFeatureGating('CUSTOM_REPORTS')?.enabled ||
              restrictViewChange
            }
          >
            <TextStyle>
              {getTranslatedText(intl, 'common.label.custom')}
            </TextStyle>
          </Button>
        </ButtonGroup>
      </div>

      <div className="fh scroll">
        <>
          {activeView === DEFAULT_VIEW && isNewContractEnabled && (
            <AccessControl
              roleToCheck="efficiencyReports.allow"
              productToCheck="MSP"
              render={
                <Section
                  showCreateBtn={activeView !== DEFAULT_VIEW}
                  loading={activeViewLoading}
                  waitForLoading={activeReportLoading}
                  title="reports.growth.report.title"
                  ldFlag="reports"
                  intl={intl}
                  isLocalised
                  data={data.profitablityReport}
                  showConfiguration={
                    isEditAuthorised && contractReportsAvailable
                  }
                  onConfigurationClick={handleGrowthModalChange}
                  checkReportAccessiable
                  featureLocked={(name: String) =>
                    checkForGating(name, EFFICIENCY_REPORT)
                  }
                />
              }
            />
          )}
          <Section
            showCreateBtn={activeView !== DEFAULT_VIEW}
            loading={activeViewLoading}
            waitForLoading={activeReportLoading}
            title="reports.dashboard.title"
            ldFlag="reports"
            intl={intl}
            onCreate={() => history.push(`/report/${NEWREPORTID}/new`)}
            data={data.report}
            addToolTip="reports.dashboard.create.btn.tooltip"
          />
          {activeView === DEFAULT_VIEW && (
            <Section
              showCreateBtn={false}
              loading={activeViewLoading}
              waitForLoading={activeReportLoading}
              title="reports.pdf.report.title"
              ldFlag="reports"
              intl={intl}
              data={data.pdfReport}
              showConfiguration={isEditAuthorised}
              onConfigurationClick={handleConfigurationModalChange}
              addToolTip="reports.dashboard.create.btn.tooltip"
            />
          )}

          {activeView === DEFAULT_VIEW && !isNewContractEnabled && (
            <AccessControl
              roleToCheck="efficiencyReports.allow"
              productToCheck="MSP"
              render={
                <Section
                  showCreateBtn={activeView !== DEFAULT_VIEW}
                  loading={activeViewLoading}
                  waitForLoading={activeReportLoading}
                  title="reports.efficiency.report.title"
                  ldFlag="reports"
                  intl={intl}
                  isLocalised
                  data={data.efficiencyReport}
                  addToolTip="reports.dashboard.create.btn.tooltip"
                />
              }
              featureGateName="EFFICIENCY_REPORTS"
            />
          )}
          {useCheckFeatureGating('DATA_REPORTS')?.enabled && (
            <Section
              showCreateBtn={activeView !== DEFAULT_VIEW}
              loading={activeViewLoading}
              waitForLoading={activeReportLoading}
              onNavClick={(item) => {
                setActiveSection({
                  section: DATA_REPORT,
                  data: item
                });
              }}
              title="reports.data.report.title"
              ldFlag="reports"
              intl={intl}
              onCreate={() => {
                setOpenDataReport(true);
              }}
              data={data.dataReport}
              addToolTip="reports.data.reports.create.btn.tooltip"
            />
          )}

          <Section
            showCreateBtn={activeView !== DEFAULT_VIEW}
            loading={activeViewLoading}
            onNavClick={(item) => {
              setActiveSection({
                section: CHARTS,
                data: item
              });
            }}
            title="reports.widget.title"
            ldFlag={CHARTS}
            intl={intl}
            onCreate={() => {
              setOpenChart(true);
            }}
            data={data.CHARTS}
            addToolTip="reports.widget.create.btn.tooltip"
          />

          <>
            {openChart && (
              <ChartHome callBack={() => closeChartModal()} mode="create" />
            )}
            {openDataReport && (
              <DataReportCascade
                callBack={() => closeDataReportModal()}
                mode="create"
              />
            )}
            {openConfigurationModal && (
              <PDFConfigurationModal
                callBack={handleConfigurationModalChange}
              />
            )}
            {openProfitablityModal && (
              <GrowthReportConfig callBack={handleGrowthModalChange} />
            )}
          </>
        </>
      </div>
    </>
  );
};

export default withRouter(NavigationContainer);
